import React, { useState, useEffect } from "react";
import { db, storage, auth } from "../../firebase";
import { collection, addDoc, doc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import "../../admin/AdminDashboard.css";

const GOOGLE_API_KEY = "AIzaSyA93FPo9uPGHk0g8FFvaZSKokn4n1tLmag";

export default function AddTrail() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [description, setDescription] = useState("");
  const [mapURL, setMapURL] = useState("");

  const [isPaid, setIsPaid] = useState(false);
  const [price, setPrice] = useState(0);

  const [bannerImage, setBannerImage] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  const [loading, setLoading] = useState(false);
  
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const data = { uid: user.uid, ...userDocSnap.data() };
            setUserData(data);
            
            if (data.role !== "admin" && data.role !== "editor") {
              alert("You don't have permission to add trails");
              navigate("/dashboard");
            }
          } else {
            alert("User account not found");
            navigate("/dashboard");
          }
        } catch (error) {
          alert("Error loading user data");
          navigate("/dashboard");
        }
      } else {
        navigate("/login");
      }
      setUserLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  function isValidCoordinates(coordString) {
    if (!coordString) return true;
    const parts = coordString.split(",");
    if (parts.length !== 2) return false;
    
    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());
    
    return !isNaN(lat) && !isNaN(lng) && 
           lat >= -90 && lat <= 90 && 
           lng >= -180 && lng <= 180;
  }

  function handleBannerSelect(e) {
    const file = e.target.files[0];
    if (file) {
      setBannerImage(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }

  async function uploadImage(file, path) {
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  }

  function safeFolderName(str) {
    return str
      .trim()
      .replace(/[/\\]/g, "")
      .replace(/[^a-zA-Z0-9_-]/g, "_");
  }

  function canCreateInCounty(county) {
    if (!userData || userData.role !== "editor") return false;
    return (userData.allowedCounties || []).includes(county);
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!name.trim()) {
      alert("Please enter a trail name.");
      return;
    }

    if (!city) {
      alert("Please select a county.");
      return;
    }

    if (userData.role === "editor" && !canCreateInCounty(city)) {
      alert(`You don't have permission to create trails in ${city}. Your assigned counties are: ${userData.allowedCounties.join(", ")}`);
      return;
    }

    if (!description.trim()) {
      alert("Please enter a description.");
      return;
    }

    if (!bannerImage) {
      alert("Please upload a banner image.");
      return;
    }

    if (mapURL && !isValidCoordinates(mapURL)) {
      alert("Please enter valid coordinates in the format: latitude, longitude");
      return;
    }

    setLoading(true);

    try {
      const safeName = safeFolderName(name);
      
      const bannerURL = await uploadImage(
        bannerImage,
        `trails/${safeName}/banner_${Date.now()}.jpg`
      );

      await addDoc(collection(db, "trails"), {
        name: name.trim(),
        city,
        description: description.trim(),
        mapURL: mapURL.trim() || "",
        mapType: "google",
        isPaid,
        priceEUR: isPaid ? Number(price) : 0,
        isLaunched: false,
        bannerImageURL: bannerURL,
        createdBy: currentUser.uid,
        createdByName: userData.name || currentUser.email,
        createdAt: new Date().toISOString(),
        lastEditedBy: currentUser.uid,
        lastEditedAt: new Date().toISOString(),
      });

      alert("Trail created successfully! You can now add clues to this trail.");
      navigate("/trailseditor");

    } catch (err) {
      alert("Error creating trail: " + err.message);
    }

    setLoading(false);
  }

  if (userLoading) {
    return (
      <div className="dash-page">
        <div className="dash-loader">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  const allowedCounties = userData.role === "admin" 
    ? ["Ireland", "Antrim", "Armagh", "Carlow", "Cavan", "Clare", "Cork", "Derry", "Donegal", "Down", "Dublin", "Fermanagh", "Galway", "Kerry", "Kildare", "Kilkenny", "Laois", "Leitrim", "Limerick", "Longford", "Louth", "Mayo", "Meath", "Monaghan", "Offaly", "Roscommon", "Sligo", "Tipperary", "Tyrone", "Waterford", "Westmeath", "Wexford", "Wicklow"]
    : (userData.allowedCounties || []);

  return (
    <div className="dash-page">
      <div className="dash-container">
        <div style={{ marginBottom: "20px" }}>
          <button
            className="dash-btn secondary"
            onClick={() => navigate("/trailseditor")}
            style={{ width: "auto" }}
          >
            ← Back to Trails Editor
          </button>
        </div>

        <header className="dash-header">
          <div className="dash-header-content">
            <h1 className="dash-title">Create New Trail</h1>
            <p className="dash-subtitle">
              Create a new trail in: <strong>{allowedCounties.join(", ")}</strong>
            </p>
          </div>
        </header>

        <form className="admin-form-large" onSubmit={handleSubmit}>
          
          {/* BASIC INFORMATION */}
          <div className="section-box">
            <h2 className="section-title">📋 Basic Information</h2>

            <label className="admin-label">Trail Name *</label>
            <input
              type="text"
              className="admin-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter a unique trail name..."
              required
            />

            <label className="admin-label" style={{ marginTop: "16px" }}>
              County *
              <span style={{ fontSize: "0.9em", color: "var(--color-success)", marginLeft: "8px" }}>
                (Your assigned counties only)
              </span>
            </label>
            <select
              className="admin-input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
            >
              <option value="">Select County</option>
              {allowedCounties.map((county) => (
                <option key={county} value={county}>
                  {county}
                </option>
              ))}
            </select>
            
            {allowedCounties.length === 0 && (
              <p style={{ color: "var(--color-error)", fontSize: "0.9em", marginTop: "8px" }}>
                ⚠️ You don't have any counties assigned. Please contact an administrator.
              </p>
            )}

            <label className="admin-label" style={{ marginTop: "16px" }}>Description *</label>
            <textarea
              className="admin-input admin-textarea"
              rows="6"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the trail, its theme, difficulty level, and what makes it special..."
              required
            />
          </div>

          {/* PRICING */}
          <div className="section-box">
            <h2 className="section-title">💰 Pricing</h2>

            <label className="admin-label">Trail Type</label>
            <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer", color: "var(--color-text)" }}>
                <input
                  type="radio"
                  name="trailType"
                  checked={!isPaid}
                  onChange={() => setIsPaid(false)}
                  style={{ marginRight: "8px" }}
                />
                <span>Free Trail</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer", color: "var(--color-text)" }}>
                <input
                  type="radio"
                  name="trailType"
                  checked={isPaid}
                  onChange={() => setIsPaid(true)}
                  style={{ marginRight: "8px" }}
                />
                <span>Paid Trail</span>
              </label>
            </div>

            {isPaid && (
              <>
                <label className="admin-label">Price (EUR €)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="admin-input"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  style={{ maxWidth: "200px" }}
                />
              </>
            )}
          </div>

          {/* MAP LOCATION */}
          <div className="section-box">
            <h2 className="section-title">📍 Starting Location</h2>

            <label className="admin-label">
              Coordinates (Optional)
              <span style={{ fontSize: "0.9em", color: "var(--color-text-secondary)", marginLeft: "8px" }}>
                Format: 53.2743872, -9.0518509
              </span>
            </label>
            <input
              type="text"
              className="admin-input"
              value={mapURL}
              onChange={(e) => setMapURL(e.target.value)}
              placeholder="53.2743872, -9.0518509"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            />

            {mapURL && isValidCoordinates(mapURL) && (() => {
              const parts = mapURL.split(",");
              const lat = parts[0].trim();
              const lng = parts[1].trim();
              const embedUrl = `https://www.google.com/maps/embed/v1/view?key=${GOOGLE_API_KEY}&center=${lat},${lng}&zoom=16`;

              return (
                <>
                  <p style={{ color: "var(--color-success)", fontSize: "0.9em", marginTop: "8px" }}>
                    ✓ Valid coordinates
                  </p>
                  <iframe
                    title="Map Preview"
                    style={{
                      marginTop: "12px",
                      width: "100%",
                      height: "320px",
                      border: "0",
                      borderRadius: "12px",
                      boxShadow: "var(--shadow-md)"
                    }}
                    src={embedUrl}
                    loading="lazy"
                  />
                </>
              );
            })()}

            {mapURL && !isValidCoordinates(mapURL) && (
              <p style={{ color: "var(--color-error)", fontSize: "0.9em", marginTop: "8px" }}>
                ⚠️ Invalid format. Use: latitude, longitude (e.g., 53.2743872, -9.0518509)
              </p>
            )}
          </div>

          {/* BANNER IMAGE */}
          <div className="section-box">
            <h2 className="section-title">🖼️ Banner Image *</h2>

            {bannerPreview && (
              <div style={{ marginBottom: "20px" }}>
                <img
                  src={bannerPreview}
                  alt="Banner Preview"
                  className="banner-preview"
                />
                <button
                  type="button"
                  className="dash-btn secondary"
                  onClick={() => {
                    setBannerImage(null);
                    setBannerPreview(null);
                  }}
                  style={{ width: "auto", fontSize: "0.9em" }}
                >
                  Remove Image
                </button>
              </div>
            )}

            <label className="admin-label">
              {bannerPreview ? "Replace Banner Image" : "Upload Banner Image"}
            </label>
            <input
              type="file"
              accept="image/*"
              className="admin-input"
              onChange={handleBannerSelect}
              required={!bannerImage}
            />
            
            <p style={{ fontSize: "0.85em", color: "var(--color-text-secondary)", marginTop: "8px" }}>
              Recommended: 1200x400px, JPG or PNG
            </p>
          </div>

          {/* ACTION BUTTONS */}
          <div style={{ 
            display: "flex", 
            gap: "12px", 
            marginTop: "30px",
            paddingTop: "20px",
            borderTop: "2px solid var(--color-border)"
          }}>
            <button 
              type="submit" 
              className="dash-btn primary" 
              disabled={loading || allowedCounties.length === 0}
              style={{ flex: 1 }}
            >
              {loading ? "Creating Trail..." : "✨ Create Trail"}
            </button>

            <button
              type="button"
              className="dash-btn secondary"
              onClick={() => navigate("/trailseditor")}
              disabled={loading}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
          </div>

          {/* HELP TEXT */}
          <div style={{ 
            marginTop: "20px", 
            padding: "15px 20px", 
            background: "rgba(16, 185, 129, 0.1)", 
            border: "1px solid rgba(16, 185, 129, 0.2)",
            borderRadius: "10px",
            fontSize: "0.9em",
            color: "var(--color-success)"
          }}>
            <p style={{ margin: 0 }}>
              💡 <strong>Next Step:</strong> After creating the trail, you can add clues, case files, 
              and success images by clicking "Manage Clues" from your dashboard.
            </p>
          </div>

        </form>
      </div>
    </div>
  );
}